'use client';

import { setCookie, parseCookies, destroyCookie } from 'nookies';

const COOKIE_NAME = 'pierre_first_touch_utm';
const COOKIE_MAX_AGE = 2 * 24 * 60 * 60; // 2 dias em segundos

export type FirstTouchUTM = {
  first_utm_source?: string;
  first_utm_medium?: string;
  first_utm_campaign?: string;
  first_utm_content?: string;
  first_utm_term?: string;
  first_url?: string;
  captured_at?: string;
};

/**
 * Captura parâmetros UTM da URL atual e salva no cookie (first-touch)
 * Só captura se utm_source estiver presente
 * Não sobrescreve cookie existente (first-touch attribution)
 */
export function captureFirstTouchUTM(): void {
  // Client-side only
  if (typeof window === 'undefined') return;

  // Verificar se já existe cookie (first-touch - não sobrescrever)
  const existingData = getFirstTouchUTM();

  // Extrair UTMs da URL
  const urlParams = new URLSearchParams(window.location.search);
  const utmSource = urlParams.get('utm_source');

  // Só capturar se utm_source estiver presente
  if (!utmSource) {
    return;
  }

  const utmData: FirstTouchUTM = {
    first_utm_source: utmSource,
    first_utm_medium: urlParams.get('utm_medium') || undefined,
    first_utm_campaign: urlParams.get('utm_campaign') || undefined,
    first_utm_content: urlParams.get('utm_content') || undefined,
    first_utm_term: urlParams.get('utm_term') || undefined,
    first_url: `${window.location.origin}${window.location.pathname}`,
    captured_at: new Date().toISOString(),
  };

  // Salvar no cookie
  try {
    setCookie(null, COOKIE_NAME, JSON.stringify(utmData), {
      maxAge: COOKIE_MAX_AGE,
      path: '/',
      sameSite: 'lax',
      secure: process.env.NODE_ENV === 'production',
    });
  } catch (error) {
    console.error('[UTM Tracker] Erro ao salvar cookie:', error);
  }
}

/**
 * Lê os dados de UTM do cookie
 */
export function getFirstTouchUTM(): FirstTouchUTM | null {
  // Client-side only
  if (typeof window === 'undefined') return null;

  try {
    const cookies = parseCookies(null);
    const cookieData = cookies[COOKIE_NAME];

    if (!cookieData) return null;

    const data = JSON.parse(cookieData) as FirstTouchUTM;
    return data;
  } catch (error) {
    console.error('[UTM Tracker] Erro ao ler cookie:', error);
    return null;
  }
}

const IFP_CAMPAIGN_COOKIE_NAME = 'ifp_campaign_params';

/**
 * Allowlist de chaves aceitas do cookie ifp_campaign_params.
 * A LP (lp.pierre.finance via Webflow) pode incluir mais chaves no cookie;
 * somente estas são repassadas ao RudderStack.
 *
 * Nota: o middleware (PR #3123) tem sua própria allowlist com 12 chaves (UTMs da URL).
 * Aqui incluímos também fbp, ttp e browser_id, que são setados client-side pelo
 * Webflow e não transitam pela URL.
 */
const IFP_ALLOWED_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_name',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'ttclid',
  'click_id',
  'wbraid',
  'gbraid',
  'fbp',
  'ttp',
  'browser_id',
] as const;

type IFPAllowedKey = (typeof IFP_ALLOWED_KEYS)[number];
const IFP_ALLOWED_SET = new Set<string>(IFP_ALLOWED_KEYS);

export type IfpCampaignParams = Partial<Record<IFPAllowedKey, string>>;

/**
 * Lê o cookie ifp_campaign_params setado pela LP (lp.pierre.finance via Webflow),
 * filtra apenas as chaves da allowlist e retorna o objeto de parâmetros.
 */
export function getIfpCampaignParams(): IfpCampaignParams | null {
  if (typeof window === 'undefined') return null;

  try {
    const cookies = parseCookies(null);
    const raw = cookies[IFP_CAMPAIGN_COOKIE_NAME];
    if (!raw) return null;

    // parseCookies() already decodes cookie values. Try direct parse first;
    // fall back to decodeURIComponent only if needed (e.g. double-encoded by Webflow).
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = JSON.parse(decodeURIComponent(raw));
    }
    if (typeof parsed !== 'object' || parsed === null) return null;
    const params = parsed as Record<string, unknown>;

    const filtered: IfpCampaignParams = {};
    for (const key of IFP_ALLOWED_SET) {
      if (
        params[key] !== undefined &&
        params[key] !== null &&
        params[key] !== ''
      ) {
        filtered[key as IFPAllowedKey] = String(params[key]);
      }
    }

    return Object.keys(filtered).length > 0 ? filtered : null;
  } catch {
    return null;
  }
}

/**
 * Mapeamento de chaves ifpCampaignParams → first_* equivalentes no firstTouchUTM.
 * Usado por mergeTrackingParams para evitar duplicação.
 */
const IFP_TO_FIRST_TOUCH_MAP: Record<string, keyof FirstTouchUTM> = {
  utm_source: 'first_utm_source',
  utm_medium: 'first_utm_medium',
  utm_campaign: 'first_utm_campaign',
  utm_content: 'first_utm_content',
  utm_term: 'first_utm_term',
};

/**
 * Merge inteligente dos dois cookies de tracking para o identify.
 * Mesma filosofia do middleware (#3123): completa sem sobrescrever.
 *
 * Regras:
 * 1. firstTouchUTM (first_*) é base — nunca sobrescrito
 * 2. ifpCampaignParams com equivalente first_* já preenchido → ignorado
 * 3. ifpCampaignParams sem equivalente first_* (gclid, fbclid, fbp, etc.) → incluído
 * 4. Fallback: se first_utm_campaign estiver vazio e utm_name existir → usa como first_utm_campaign
 */
export function mergeTrackingParams(
  firstTouch: FirstTouchUTM | null,
  ifpParams: IfpCampaignParams | null,
): Record<string, string> {
  const result: Record<string, string> = {};

  // 1. Spread firstTouchUTM como base
  if (firstTouch) {
    for (const [key, value] of Object.entries(firstTouch)) {
      if (value !== undefined && value !== null && value !== '') {
        result[key] = String(value);
      }
    }
  }

  if (!ifpParams) return result;

  // 4. Fallback: utm_name → first_utm_campaign se vazio
  if (!result.first_utm_campaign && ifpParams.utm_name) {
    result.first_utm_campaign = ifpParams.utm_name;
  }

  // 2+3. Completa com ifpCampaignParams — só chaves sem equivalente first_* já preenchido
  for (const [key, value] of Object.entries(ifpParams)) {
    if (!value) continue;

    const firstTouchEquivalent = IFP_TO_FIRST_TOUCH_MAP[key];

    if (firstTouchEquivalent) {
      // Mapped key (utm_source → first_utm_source, etc.)
      if (result[firstTouchEquivalent]) {
        // first_* already filled → skip (don't duplicate)
        continue;
      }
      // first_* is empty → use ifpCampaignParams value as first_* equivalent
      result[firstTouchEquivalent] = value;
    } else {
      // Unmapped key (gclid, fbclid, fbp, ttp, browser_id, etc.) → include directly
      result[key] = value;
    }
  }

  return result;
}

/**
 * Remove o cookie de UTM (útil para testes)
 */
export function clearFirstTouchUTM(): void {
  // Client-side only
  if (typeof window === 'undefined') return;

  try {
    destroyCookie(null, COOKIE_NAME, { path: '/' });
  } catch (error) {
    console.error('[UTM Tracker] Erro ao remover cookie:', error);
  }
}
